import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, ChevronLeft, ChevronRight, Maximize2, Check, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getTeamLeaveCalendar, postManagerApproveLeave, postManagerDeclineLeave } from "api/apiHelper";
import { toast } from "react-toastify";

// Types
interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  designation: string;
  email: string;
  phone: string;
  photo: string | null;
}

interface LeaveType {
  id: number;
  description: string;
  is_paid: boolean;
}

interface Approver {
  id: number;
  full_name: string;
}

interface Leave {
  id: number;
  start_date: string;
  end_date: string;
  working_days: number;
  leave_status: 'A' | 'MA' | 'P' | 'D' | 'MD';
  leave_status_display: string;
  leave_type: LeaveType;
  reason: string;
  comments: string;
  requested_at: string | null;
  employee: Employee;
  approvals: {
    manager_approved_by: Approver | null;
    approved_by: Approver | null;
  };
}

interface CalendarData {
  manager: {
    id: number;
    full_name: string;
    designation: string;
  };
  team_size: number;
  team_members: Employee[];
  leaves: Leave[];
  total_leaves: number;
  filters_applied: {
    start_date: string | null;
    end_date: string | null;
    status: string;
    employee_id: string | null;
  };
}



const ManagerCalendarView: React.FC = ({ 
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedDay, setSelectedDay] = useState<{ day: number; leaves: Leave[] } | null>(null);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [processingLeaveId, setProcessingLeaveId] = useState<number | null>(null);
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);

  const statusColors: Record<'A' | 'MA' | 'P' | 'D' | 'MD', string> = {
    'A': 'bg-green-100 border-green-500 text-green-800',
    'MA': 'bg-blue-100 border-blue-500 text-blue-800',
    'P': 'bg-yellow-100 border-yellow-500 text-yellow-800',
    'D': 'bg-red-100 border-red-500 text-red-800',
    'MD': 'bg-gray-100 border-gray-500 text-gray-800',
  };

  const dotColors: Record<'A' | 'MA' | 'P' | 'D' | 'MD', string> = {
    'A': 'bg-green-500',
    'MA': 'bg-blue-500',
    'P': 'bg-yellow-500',
    'D': 'bg-red-500',
    'MD': 'bg-gray-500',
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showModal && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);

  const fetchCalendarData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const response = await getTeamLeaveCalendar({ 
        start_date: startDate, 
        end_date: endDate,
        status: 'A,MA,P' // Include Pending, Approved, and Manager Approved leaves
      });
      setCalendarData(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (): (number | null)[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getLeavesForDay = (day: number | null): Leave[] => {
    if (!calendarData || !day) return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const currentDayDate = new Date(year, month, day);
    
    // Check if it's a weekend (Saturday=6 or Sunday=0)
    const dayOfWeek = currentDayDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return []; // Don't show leaves on weekends
    }
    
    // Format as YYYY-MM-DD in local timezone
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return calendarData.leaves.filter((leave: Leave) => {
      return dateStr >= leave.start_date && dateStr <= leave.end_date;
    });
  };

  const handleDayClick = (day: number | null) => {
    if (!day) return;
    const leaves = getLeavesForDay(day);
    if (leaves.length > 0) {
      setSelectedDay({ day, leaves });
      setShowModal(true);
    }
  };

  const changeMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isToday = (day: number | null): boolean => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  const handleApprove = async (leaveId: number) => {
    setProcessingLeaveId(leaveId);
    const notification = toast.loading("Manager Approving Leave...");

    try {
      await postManagerApproveLeave(leaveId.toString(), comments[leaveId] || '');
      toast.update(notification, {
        render: "Leave Manager Approved successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Clear comment for this leave
      setComments(prev => {
        const newComments = { ...prev };
        delete newComments[leaveId];
        return newComments;
      });
      // Refresh calendar data
      await fetchCalendarData();
      // Update selected day leaves
      if (selectedDay) {
        const updatedLeaves = selectedDay.leaves.map(leave => 
          leave.id === leaveId ? { ...leave, leave_status: 'MA' as const, leave_status_display: 'Manager Approved' } : leave
        );
        setSelectedDay({ ...selectedDay, leaves: updatedLeaves });
      }
    } catch (error: any) {
      console.log(error);
      let errorMessage = "Failed to approve leave";
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (errorMessage.length > 80) {
          errorMessage = "Failed to approve leave";
        }
      }
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingLeaveId(null);
    }
  };

  const handleDecline = async (leaveId: number) => {
    setProcessingLeaveId(leaveId);
    const notification = toast.loading("Manager Declining Leave...");

    try {
      await postManagerDeclineLeave(leaveId.toString(), comments[leaveId] || '');
      toast.update(notification, {
        render: "Leave Manager Declined successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Clear comment for this leave
      setComments(prev => {
        const newComments = { ...prev };
        delete newComments[leaveId];
        return newComments;
      });
      // Refresh calendar data
      await fetchCalendarData();
      // Update selected day leaves
      if (selectedDay) {
        const updatedLeaves = selectedDay.leaves.map(leave => 
          leave.id === leaveId ? { ...leave, leave_status: 'MD' as const, leave_status_display: 'Manager Declined' } : leave
        );
        setSelectedDay({ ...selectedDay, leaves: updatedLeaves });
      }
    } catch (error: any) {
      console.log(error);
      toast.update(notification, {
        render: "Failed to decline Leave",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingLeaveId(null);
    }
  };

  const handleCommentChange = (leaveId: number, value: string) => {
    setComments(prev => ({
      ...prev,
      [leaveId]: value
    }));
  };


  if (isLoading) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden p-6">
        <div className="text-red-600 dark:text-red-400">Error: {error}</div>
      </div>
    );
  }

  if (!calendarData || calendarData.team_size === 0) {
    return null;
  }

  const days = getDaysInMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-blue-600 dark:text-blue-400" size={24} />
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Team Leave Calendar
              </h2>
            </div>
            <button
              onClick={() => navigate('/calendar')}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 transition-colors text-sm font-medium"
            >
              <Calendar size={16} />
                Calendar
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              {monthName}
            </h3>
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day: string) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2"
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {days.map((day: number | null, index: number) => {
              const leaves = day ? getLeavesForDay(day) : [];
              const hasLeaves = leaves.length > 0;
              const today = day && isToday(day);

              return (
                <div
                  key={index}
                  onClick={() => day && handleDayClick(day)}
                  className={`
                    min-h-[60px] p-2 border border-gray-200 dark:border-gray-700 rounded-lg
                    ${day ? 'bg-white dark:bg-navy-700' : 'bg-gray-50 dark:bg-navy-900'}
                    ${hasLeaves ? 'cursor-pointer hover:ring-2 hover:ring-blue-400' : ''}
                    ${today ? 'ring-2 ring-blue-500' : ''}
                  `}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${today ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                        {day}
                      </div>
                      {hasLeaves && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Array.from(new Set(leaves.map(l => l.leave_status))).map((status, idx) => (
                            <div
                              key={idx}
                              className={`w-2 h-2 rounded-full ${dotColors[status]}`}
                              title={`${status === 'A' ? 'Approved' : status === 'MA' ? 'Manager Approved' : status === 'P' ? 'Pending' : status === 'D' ? 'Declined' : 'Manager Declined'}`}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600 dark:text-gray-400">Approved</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600 dark:text-gray-400">Manager Approved</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-600 dark:text-gray-400">Pending</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Leave Details with Blur Backdrop */}
      {showModal && selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Leaves on {monthName.split(' ')[0]} {selectedDay.day}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              <div className="space-y-4">
                {selectedDay.leaves.map((leave: Leave, idx: number) => (
                  <div
                    key={idx}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      {leave.employee.photo ? (
                        <img
                          src={leave.employee.photo}
                          alt={leave.employee.full_name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold">
                            {leave.employee.first_name[0]}{leave.employee.last_name[0]}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-800 dark:text-white truncate">
                              {leave.employee.full_name}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {leave.employee.designation}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${statusColors[leave.leave_status]}`}>
                            {leave.leave_status_display}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-2">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Leave Type:</span>
                            <span className="ml-2 font-medium text-gray-700 dark:text-gray-300">
                              {leave.leave_type.description}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Duration:</span>
                            <span className="ml-2 font-medium text-gray-700 dark:text-gray-300">
                              {leave.working_days} day{leave.working_days !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="col-span-1 sm:col-span-2">
                            <span className="text-gray-500 dark:text-gray-400">Period:</span>
                            <span className="ml-2 font-medium text-gray-700 dark:text-gray-300">
                              {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {leave.reason && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Reason:</span>
                            <p className="mt-1 text-gray-700 dark:text-gray-300">{leave.reason}</p>
                          </div>
                        )}

                        {/* Approve/Decline buttons for pending requests */}
                        {leave.leave_status === 'P' && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Comments (optional)
                                </label>
                                <textarea
                                  value={comments[leave.id] || ''}
                                  onChange={(e) => handleCommentChange(leave.id, e.target.value)}
                                  placeholder="Enter comments here..."
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-navy-700 text-gray-900 dark:text-white text-sm resize-none"
                                  rows={2}
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApprove(leave.id)}
                                  disabled={processingLeaveId === leave.id}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors text-sm font-medium disabled:cursor-not-allowed"
                                >
                                  <Check size={16} />
                                  {processingLeaveId === leave.id ? 'Processing...' : 'Approve'}
                                </button>
                                <button
                                  onClick={() => handleDecline(leave.id)}
                                  disabled={processingLeaveId === leave.id}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors text-sm font-medium disabled:cursor-not-allowed"
                                >
                                  <XCircle size={16} />
                                  {processingLeaveId === leave.id ? 'Processing...' : 'Decline'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerCalendarView;